import type { ReactElement, ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import { MarketingLayout } from "../sections/Layout";
import { cn } from "@/lib/utils";

/** One numbered top-level section. `id` is the anchor and the TOC link target. */
export interface LegalSection {
  id: string;
  title: string;
  body: ReactNode;
}

interface DocProps {
  doc: "terms" | "privacy";
  title: string;
  summary: ReactNode;
  effective: string;
  updated: string;
  sections: readonly LegalSection[];
}

export function LegalDoc({
  doc,
  title,
  summary,
  effective,
  updated,
  sections,
}: DocProps): ReactElement {
  return (
    <MarketingLayout>
      <main className="mx-auto w-full max-w-3xl px-6 py-16 sm:py-24">
        <p className="text-sm font-medium uppercase tracking-wider text-primary">
          Legal
        </p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight sm:text-5xl">
          {title}
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Effective {effective} · Last updated {updated}
        </p>

        <nav className="mt-6 flex gap-6 text-sm">
          <DocTab doc={doc} target="terms" label="Terms of Service" />
          <DocTab doc={doc} target="privacy" label="Privacy Policy" />
          <Link
            to="/legal/sms"
            className="text-muted-foreground transition-colors hover:text-foreground"
          >
            SMS program
          </Link>
        </nav>

        <div className="mt-10 text-base leading-relaxed text-foreground/80">
          {summary}
        </div>

        <ol className="mt-10 grid gap-x-8 gap-y-1 border-y border-border/60 py-6 text-sm sm:grid-cols-2">
          {sections.map((section, index) => (
            <li key={section.id} className="flex gap-2">
              <span className="tabular-nums text-muted-foreground">
                {index + 1}.
              </span>
              <a
                href={`#${section.id}`}
                className="text-muted-foreground transition-colors hover:text-foreground"
              >
                {section.title}
              </a>
            </li>
          ))}
        </ol>

        <div className="mt-12 space-y-12">
          {sections.map((section, index) => (
            <section key={section.id} id={section.id} className="scroll-mt-24">
              <h2 className="text-2xl font-semibold tracking-tight">
                <span className="mr-2 tabular-nums text-muted-foreground">
                  {index + 1}.
                </span>
                {section.title}
              </h2>
              <div className="mt-4 space-y-4 text-base leading-relaxed text-foreground/90">
                {section.body}
              </div>
            </section>
          ))}
        </div>
      </main>
    </MarketingLayout>
  );
}

function DocTab({
  doc,
  target,
  label,
}: {
  doc: string;
  target: "terms" | "privacy";
  label: string;
}): ReactElement {
  return (
    <Link
      to="/legal/$doc"
      params={{ doc: target }}
      className={cn(
        "transition-colors",
        doc === target
          ? "text-primary"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {label}
    </Link>
  );
}

/** An unnumbered heading inside a section. */
export function Sub({ children }: { children: ReactNode }): ReactElement {
  return (
    <h3 className="pt-2 text-base font-semibold text-foreground">{children}</h3>
  );
}

export function Bullets({
  items,
}: {
  items: readonly ReactNode[];
}): ReactElement {
  return (
    <ul className="list-disc space-y-2 pl-6">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export function Steps({
  items,
}: {
  items: readonly ReactNode[];
}): ReactElement {
  return (
    <ol className="list-decimal space-y-2 pl-6">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ol>
  );
}

/** A simple bordered table; scrolls horizontally on narrow screens. */
export function LegalTable({
  headers,
  rows,
}: {
  headers: readonly string[];
  rows: readonly (readonly ReactNode[])[];
}): ReactElement {
  return (
    <div className="-mx-6 overflow-x-auto px-6 sm:mx-0 sm:px-0">
      <table className="w-full min-w-[34rem] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            {headers.map((header) => (
              <th
                key={header}
                scope="col"
                className="py-2 pr-4 text-left align-bottom font-semibold text-foreground"
              >
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-border/50 align-top">
              {row.map((cell, j) => (
                <td key={j} className="py-3 pr-4 leading-relaxed">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Mail({ address = "hello@getchill.ai" }: { address?: string }) {
  return (
    <a href={`mailto:${address}`} className="text-primary hover:underline">
      {address}
    </a>
  );
}
